import {
  ActionList,
  ActionListItem,
  Dialog,
  DialogContent,
  DialogHeader,
  Icon,
  LinkCard,
  LinkCardButton,
  LinkCardContent,
  LinkCardDescription,
  LinkCardHeading,
  LinkCardIcon,
} from "@prismicio/editor-ui"
import { Link } from "react-router-dom"
import { z } from "zod"

import { trackDemoCta } from "../../analytics/tracking"
import styles from "./GetStartedDialog.module.css"

type GetStartedDialogProps = {
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

const frameworkUrls = {
  next: "https://prismic.io/dashboard/new-repository?framework=next",
  nuxt: "https://prismic.io/dashboard/new-repository?framework=nuxt",
  svelte: "https://prismic.io/dashboard/new-repository?framework=sveltekit",
}

export function GetStartedDialog(props: GetStartedDialogProps) {
  const { open = false, onOpenChange } = props
  const params = getSearchParams({ source: z.enum(["value-first"]).optional() })

  return (
    <Dialog
      open={open}
      size={{
        // Full width minus the 24px padding on the left and right. Max width is 700px on large screens.
        width: "min(calc(100% - 48px), 700px)",
        height: "auto",
      }}
      onOpenChange={onOpenChange}
    >
      <DialogHeader icon="prismic" title="Get started with Prismic" />
      <DialogContent>
        <div className={styles["content"]}>
          {params.source === "value-first" ? (
            <LinkCard>
              <LinkCardIcon name="code" />
              <LinkCardContent>
                <LinkCardHeading>Create a repository</LinkCardHeading>
                <LinkCardDescription>
                  Choose your framework and start developing.
                </LinkCardDescription>
                <ActionList>
                  <ActionListItem
                    asChild
                    renderStartIcon={() => (
                      <Icon name="nextjs" size="extraSmall" />
                    )}
                    endIcon="arrowForward"
                    onClick={() => trackCreateRepository(frameworkUrls.next)}
                  >
                    <Link to={frameworkUrls.next}>Next.js</Link>
                  </ActionListItem>
                  <ActionListItem
                    asChild
                    renderStartIcon={() => (
                      <Icon name="nuxt" size="extraSmall" />
                    )}
                    endIcon="arrowForward"
                    onClick={() => trackCreateRepository(frameworkUrls.nuxt)}
                  >
                    <Link to={frameworkUrls.nuxt}>Nuxt</Link>
                  </ActionListItem>
                  <ActionListItem
                    asChild
                    renderStartIcon={() => (
                      <Icon name="svelte" size="extraSmall" />
                    )}
                    endIcon="arrowForward"
                    onClick={() => trackCreateRepository(frameworkUrls.svelte)}
                  >
                    <Link to={frameworkUrls.svelte}>SvelteKit</Link>
                  </ActionListItem>
                </ActionList>
              </LinkCardContent>
            </LinkCard>
          ) : (
            <LinkCard
              href="https://prismic.io/dashboard/signup"
              target="_blank"
              onClick={() =>
                trackDemoCta("Sign up", {
                  url: "https://prismic.io/dashboard/signup",
                  position: "get started modal",
                  type: "primary",
                })
              }
            >
              <LinkCardIcon name="code" />
              <LinkCardContent>
                <LinkCardHeading>Start developing</LinkCardHeading>
                <LinkCardDescription>
                  Ready to take your content creation experience to the next
                  level? Set up your first project in minutes.
                </LinkCardDescription>
              </LinkCardContent>
              <LinkCardButton footnote="Coding experience is required">
                Sign up
              </LinkCardButton>
            </LinkCard>
          )}

          <LinkCard
            href="https://prismic.io/demo"
            target="_blank"
            onClick={() =>
              trackDemoCta("Schedule a demo", {
                url: "https://prismic.io/demo",
                position: "get started modal",
                type: "secondary",
              })
            }
          >
            <LinkCardIcon name="people" />
            <LinkCardContent>
              <LinkCardHeading>Book a personalized demo</LinkCardHeading>
              <LinkCardDescription>
                Find out how to make the most out of our product.
              </LinkCardDescription>
            </LinkCardContent>
            <LinkCardButton>Schedule a demo</LinkCardButton>
          </LinkCard>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function trackCreateRepository(url: string) {
  trackDemoCta("Create a repository", {
    url,
    position: "get started modal",
    type: "primary",
  })
}

export function getSearchParams<SearchShape extends z.ZodRawShape>(
  search: SearchShape,
) {
  const urlSearchParams = new URLSearchParams(document.location.search)
  const searchParams = Object.fromEntries(urlSearchParams)
  const { data, success } = z.object(search).safeParse(searchParams)

  return success ? data : ({} as { [k in keyof SearchShape]?: SearchShape[k] })
}
